import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, Menu, X, MapPin, Phone, MessageCircle, ClipboardList, ChevronDown } from 'lucide-react';
import { getAllCategories, getSubcategories } from '../data/catalog';
import { useSiteConfig } from '../utils/siteConfig';
import { useSolicitation } from '../context/SolicitationContext';
import { useCatalogTick } from '../utils/customProducts';

function SearchBar({ mobile = false }) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/busca?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form
      onSubmit={submit}
      role="search"
      className={mobile ? 'flex w-full' : 'flex w-full max-w-xl'}
    >
      <label htmlFor={mobile ? 'search-mobile' : 'search-desktop'} className="sr-only">
        Buscar tecidos
      </label>
      <input
        id={mobile ? 'search-mobile' : 'search-desktop'}
        data-testid={mobile ? 'header-search-input-mobile' : 'header-search-input'}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="O que você está procurando?"
        className="h-11 w-full rounded-l-full border border-r-0 border-input bg-white px-5 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
      />
      <button
        type="submit"
        data-testid={mobile ? 'header-search-button-mobile' : 'header-search-button'}
        aria-label="Buscar"
        className="h-11 rounded-r-full bg-navy px-5 text-white transition-colors hover:bg-navy-hover"
      >
        <Search className="h-4 w-4" />
      </button>
    </form>
  );
}

export function Logo({ compact = false }) {
  const { identity, store } = useSiteConfig();
  if (identity.logoImage) {
    return (
      <Link to="/" data-testid="header-logo" className="flex items-center leading-none" aria-label={`${store.name} - Página inicial`}>
        <img
          src={identity.logoImage}
          alt={store.name}
          className={`w-auto max-w-[180px] object-contain ${compact ? 'h-9' : 'h-12 md:h-14'}`}
          data-testid="header-logo-image"
        />
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="header-logo" className="leading-none" aria-label="Huddersfield Tecidos - Página inicial">
      <span className={`font-serif font-semibold tracking-wide text-navy ${compact ? 'text-xl' : 'text-2xl'}`}>
        H<span className="font-normal">UDDERSFIELD</span>
      </span>
      <span className="block text-[10px] font-semibold tracking-[0.35em] text-navy/80">TECIDOS</span>
      {!compact && (
        <span className="mt-0.5 block text-[9px] tracking-[0.25em] text-muted-foreground">
          ELEGÂNCIA PERSONALIZADA
        </span>
      )}
    </Link>
  );
}

function RequestButton() {
  const { count } = useSolicitation();
  return (
    <Link
      to="/solicitacao"
      data-testid="header-request-counter-button"
      className="relative flex items-center gap-2 rounded-full px-3 py-2 text-navy transition-colors hover:bg-navy-light"
      aria-label={`Minha Solicitação, ${count} itens`}
    >
      <ClipboardList className="h-6 w-6" />
      {count > 0 && (
        <span
          data-testid="header-request-count"
          className="absolute -top-0.5 left-6 flex h-5 min-w-5 items-center justify-center rounded-full bg-navy px-1 text-[11px] font-bold text-white"
        >
          {count}
        </span>
      )}
      <span className="hidden text-xs font-semibold leading-tight lg:block">
        Minha
        <br />
        Solicitação
      </span>
    </Link>
  );
}

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef(null);
  const navigate = useNavigate();
  const { store } = useSiteConfig();

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === 'Escape' && setDrawerOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Mega menu de categorias: fecha com Escape e com clique fora do painel.
  useEffect(() => {
    if (!megaOpen) return;
    const onKey = (e) => e.key === 'Escape' && setMegaOpen(false);
    const onClick = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [megaOpen]);
  useCatalogTick();
  // Navegação pública: barra enxuta com as primeiras categorias (ordem alfabética
  // pt-BR) + painel "Todas as categorias" com a lista completa e subcategorias
  // aninhadas. O painel elimina o bug do submenu suspenso quebrando a barra quando
  // a lista de categorias cresce.
  const sortedCategories = [...getAllCategories()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  const categoryItems = sortedCategories.map((c) => ({ to: `/categoria/${c.slug}`, label: c.name, slug: c.slug, subs: getSubcategories(c.slug) }));
  const navItems = [...categoryItems, { to: '/categoria/todos', label: 'Todos os Tecidos', slug: 'todos', subs: [] }];
  const barItems = categoryItems.slice(0, 8);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-[0_1px_0_0_rgba(15,41,66,0.08)]">
      <div className="hidden bg-navy-dark text-white/90 md:block" data-testid="header-topbar">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5" data-testid="header-topbar-address">
              <MapPin className="h-3.5 w-3.5" /> {store.address} · {store.cityUf}
            </span>
            <a href={`tel:+55${store.phone.replace(/\D/g, '')}`} className="flex items-center gap-1.5 hover:text-white" data-testid="header-topbar-phone">
              <Phone className="h-3.5 w-3.5" /> {store.phone}
            </a>
            <a
              href={`https://wa.me/${store.whatsappDigits}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 hover:text-white"
              data-testid="header-topbar-whatsapp"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp {store.whatsapp}
            </a>
          </div>
          <Link to="/nossa-loja" className="hover:text-white" data-testid="header-topbar-store-link">
            Nossa Loja
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4">
        <div className="flex items-center gap-3 py-3 md:gap-8 md:py-4">
          <button
            className="rounded-full p-2 text-navy hover:bg-navy-light md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
            data-testid="header-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="shrink-0">
            <span className="hidden md:block"><Logo /></span>
            <span className="md:hidden"><Logo compact /></span>
          </div>
          <div className="hidden flex-1 justify-center md:flex">
            <SearchBar />
          </div>
          <div className="ml-auto flex items-center gap-1 md:gap-3">
            <a
              href={`https://wa.me/${store.whatsappDigits}`}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-button"
              className="hidden items-center gap-2 rounded-full px-3 py-2 text-[#128C4B] transition-colors hover:bg-green-50 lg:flex"
            >
              <MessageCircle className="h-6 w-6" />
              <span className="text-xs font-semibold leading-tight">
                Fale pelo
                <br />
                WhatsApp
              </span>
            </a>
            <RequestButton />
          </div>
        </div>
        <div className="pb-3 md:hidden">
          <SearchBar mobile />
        </div>
      </div>

      <nav ref={megaRef} className="relative hidden border-t border-navy/10 md:block" aria-label="Categorias">
        <div className="mx-auto flex max-w-7xl items-stretch px-4" data-testid="nav-bar">
          {barItems.map((item) => (
            <NavLink
              key={item.slug}
              to={item.to}
              onClick={() => setMegaOpen(false)}
              data-testid={`nav-category-item-${item.slug}`}
              className={({ isActive }) =>
                `block px-5 py-2.5 text-sm font-medium transition-colors hover:text-navy ${
                  isActive ? 'text-navy underline underline-offset-4' : 'text-foreground/70'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMegaOpen((v) => !v)}
            aria-expanded={megaOpen}
            aria-controls="nav-mega-panel"
            data-testid="nav-mega-button"
            className={`ml-auto flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold transition-colors ${megaOpen ? 'text-navy' : 'text-foreground/80 hover:text-navy'}`}
          >
            Todas as categorias
            <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {megaOpen && (
          <div id="nav-mega-panel" className="absolute inset-x-0 top-full z-40 border-b border-navy/10 bg-white shadow-[0_18px_30px_-18px_rgba(15,41,66,0.35)]" data-testid="nav-mega-panel">
            <div className="mx-auto grid max-w-7xl grid-cols-3 gap-x-6 gap-y-1 px-4 py-5 lg:grid-cols-5">
              {categoryItems.map((item) => (
                <div key={item.slug}>
                  <NavLink
                    to={item.to}
                    onClick={() => setMegaOpen(false)}
                    data-testid={`nav-mega-item-${item.slug}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-navy-light hover:text-navy ${isActive ? 'font-semibold text-navy' : 'text-foreground/75'}`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.subs.map((s) => (
                    <Link
                      key={s.slug}
                      to={`${item.to}?sub=${s.slug}`}
                      onClick={() => setMegaOpen(false)}
                      data-testid={`nav-mega-sub-${s.slug}`}
                      className="block rounded-lg py-1.5 pl-6 pr-3 text-xs text-muted-foreground transition-colors hover:bg-navy-light hover:text-navy"
                    >
                      ↳ {s.name}
                    </Link>
                  ))}
                </div>
              ))}
              <div>
                <NavLink
                  to="/categoria/todos"
                  onClick={() => setMegaOpen(false)}
                  data-testid="nav-mega-item-todos"
                  className="block rounded-lg px-3 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy-light"
                >
                  Todos os Tecidos →
                </NavLink>
              </div>
            </div>
          </div>
        )}
      </nav>

      {drawerOpen && (
        <div className="fixed inset-0 z-[80] md:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-xs overflow-y-auto bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <Logo compact />
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Fechar menu"
                data-testid="mobile-nav-close-button"
                className="rounded-full p-2 text-navy hover:bg-navy-light"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-6 flex flex-col" aria-label="Categorias mobile">
              {navItems.map((item) => (
                <div key={item.slug}>
                  <button
                    data-testid={`mobile-nav-item-${item.slug}`}
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(item.to);
                    }}
                    className="w-full border-b border-navy/10 py-4 text-left font-serif text-lg text-navy"
                  >
                    {item.label}
                  </button>
                  {item.subs.map((s) => (
                    <button
                      key={s.slug}
                      data-testid={`mobile-nav-sub-${s.slug}`}
                      onClick={() => {
                        setDrawerOpen(false);
                        navigate(`${item.to}?sub=${s.slug}`);
                      }}
                      className="w-full border-b border-navy/10 py-3 pl-5 text-left text-sm text-foreground/70"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              ))}
            </nav>
            <div className="mt-6 space-y-3 text-sm text-muted-foreground">
              <a href={`https://wa.me/${store.whatsappDigits}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#128C4B]" data-testid="mobile-nav-whatsapp">
                <MessageCircle className="h-4 w-4" /> WhatsApp {store.whatsapp}
              </a>
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> {store.address} · {store.cityUf}
              </p>
              <Link to="/nossa-loja" onClick={() => setDrawerOpen(false)} className="inline-block font-semibold text-navy" data-testid="mobile-nav-store-link">
                Nossa Loja →
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
